import { useState, useEffect, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import api from 'services/api'
import { usePlayer } from '../Moments/MomentsBrowser'
import ReportCard, { useReportActions, PlayerAudio } from './ReportCard'

// One report's own page (step 6b): what a Slack post links to
// (`${WEB_ADMIN_URL}/reports/{id}`). Any status, a row written before step 6
// included; the actions only while it is open. Deleting the account removes the
// report with it, so after that action the page shows the row as it stood, and
// a later visit to the link finds nothing.
const Report = () => {
  const { reportId } = useParams()
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)
  const [removed, setRemoved] = useState(false)
  const player = usePlayer()

  useEffect(() => {
    let live = true
    setReport(null)
    setError(null)
    setRemoved(false)
    api.reports
      .getReport({ reportId })
      .then((row) => live && setReport(row))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [reportId])

  const onSettled = useCallback((current, action, row) => {
    if (action === 'disable_account') setRemoved(true)
    if (row) setReport(row)
  }, [])
  const actions = useReportActions(onSettled)

  return (
    <Box
      sx={{
        px: { mobile: 2, desktop: 6 },
        py: { mobile: 3, desktop: 4 },
        maxWidth: 1200,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          size="small"
          component={Link}
          to="/reports"
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          Reports
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Report
        </Typography>
      </Box>
      {error && (
        <Alert severity={error === 'not_found' ? 'info' : 'error'}>
          {error === 'not_found'
            ? 'No such report. Deleting an account removes every report about it, so a Slack link can outlive its report.'
            : error}
        </Alert>
      )}
      {!report && !error && <CircularProgress size={20} />}
      {removed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The account is deleted, and its deletion removed this report: this is
          the row as it stood when it was actioned.
        </Alert>
      )}
      {report && (
        <ReportCard
          report={report}
          player={player}
          onAct={actions.request}
          busy={actions.busy[report.id]}
        />
      )}
      <PlayerAudio player={player} />
      {actions.dialog}
    </Box>
  )
}

export default Report
